import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Alphabet.css";

interface AlphabetItem {
  id: number;
  letter: string;
  audio: string | null;
  image: string | null;
  created_at: string;
  updated_at: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Alphabet: React.FC = () => {
  const [data, setData] = useState<AlphabetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/alphabet`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching alphabet:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const playAudioOrTTS = (item: AlphabetItem) => {
    if (currentlyPlaying === item.letter) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.letter);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.letter);
      };
    } else {
      speak(item.letter);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Alphabet",
    de: "Alphabet",
    ta: "அகரவரிசை",
    si: "අක්ෂර මාලාව",
    hi: "वर्णमाला",
    ar: "الأبجدية",
    tr: "Alfabe",
    ru: "Алфавит",
    zh: "字母表",
    fr: "Alphabet",
    es: "Alfabeto",
  };

  const subtitleMap = {
    en: "German Alphabet",
    de: "Deutsches Alphabet",
    ta: "ஜெர்மன் எழுத்துக்கள்",
    si: "ජර්මන් අක්ෂර",
    hi: "जर्मन वर्णमाला",
    ar: "الأبجدية الألمانية",
    tr: "Almanca Alfabe",
    ru: "Немецкий алфавит",
    zh: "德语字母表",
    fr: "Alphabet allemand",
    es: "Alfabeto alemán",
  };

  return (
    <div className="alphabet-page-container">
      <div className="alphabet-page-header">
        <div>
          <h1 className="alphabet-page-title">{titleMap["de"]}</h1>
          <p className="alphabet-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="alphabet-page-loading">Loading...</div>
      ) : (
        <div className="alphabet-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.letter;
            return (
              <div
                key={item.id}
                className={`alphabet-page-card ${
                  isPlaying ? "alphabet-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="alphabet-page-image-wrapper">
                  <div className="alphabet-page-image-letter">
                    <span className="alphabet-page-capital-letter">
                      {capitalize(item.letter)}
                    </span>
                    <span className="alphabet-page-small-letter">
                      {item.letter.toLowerCase()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Alphabet;
